import React, { Suspense, useCallback } from 'react';
import { Container, Dialog } from '@mui/material';
import { AddWidgetBox, CustomWaterLoading } from 'src/components';
import { useSiteLocalTrendsContext } from 'src/contexts/siteLocalTrendsContext';
import { LineChartTrend } from 'src/features/trends/components/line-chart-trend';
import { useAuthentication } from 'src/features/authentication/context';
import { useToggle } from 'src/hooks/useToggle';
import { FloatingAddLogWrapper } from 'src/layouts/floatingAddLogWrapper/FloatingAddLogWrapper';
import { SelectedInputType } from 'src/features/trends/types/selected-input';
import { LineChartLocalTrend } from 'src/types/site-local-trends';

const AddChartModalContent = React.lazy(
  () => import('src/features/trends/components/add-chart-modal-content/AddChartModalContent')
);

export const SiteTrends = () => {
  const { state, onToggle } = useToggle();
  const { lineChartsLocalTrends, removeChart, addNewChart, updateExitingChart } =
    useSiteLocalTrendsContext();

  const {
    siteId: { value: siteId },
    customerId: { value: customerId },
  } = useAuthentication();

  const displayedCharts = lineChartsLocalTrends.filter(
    (chart) => chart.siteId === siteId && chart.customerId === customerId
  );

  const onSelectEventHandler = useCallback(
    (selectedInputs: SelectedInputType[], id: string) => {
      if (!customerId || !siteId) return;
      const chart: LineChartLocalTrend = {
        id,
        customerId,
        siteId,
        selectedInputs,
      };
      const isExisting = lineChartsLocalTrends.some((c) => c.id === id);
      if (isExisting) {
        updateExitingChart(chart);
      } else {
        addNewChart(chart);
      }
    },
    [lineChartsLocalTrends, customerId, siteId, updateExitingChart, addNewChart]
  );

  return (
    <>
      <Dialog
        open={state}
        onClose={onToggle}
        sx={{
          '& .MuiPaper-root': {
            width: '800px',
            overflowX: 'hidden',
            maxWidth: '100%',
            boxSizing: 'border-box',
          },
        }}
      >
        <Suspense fallback={<CustomWaterLoading />}>
          <AddChartModalContent
            onToggleHandler={onToggle}
            onSelectEventHandler={onSelectEventHandler}
            fixedSiteId={siteId!}
          />
        </Suspense>
      </Dialog>
      <Container maxWidth={false} disableGutters data-sm="site-trends-container">
        {displayedCharts?.map((chart) => (
          <LineChartTrend
            key={chart.id}
            removeChart={removeChart}
            pointChart={chart}
            isLocalTrends={true}
            onSelectEventHandler={onSelectEventHandler}
            fixedSiteId={siteId!}
          />
        ))}
        <AddWidgetBox fullWidth title="Add Chart" onToggle={onToggle} />
        <FloatingAddLogWrapper customerId={customerId!} siteId={siteId!} />
      </Container>
    </>
  );
};
